/**
 * <ec-variant-picker>
 *
 * Light-DOM Web Component that owns variant selection on the PDP.
 * Reads the variants array from `data-variants` (rendered server-side
 * by snippets/ec-product-info.liquid) and manages:
 *   - strict combination filtering: greys out option values that have
 *     no available variant for the currently checked options (e.g. once a
 *     color is picked, impossible sizes are visually struck through)
 *   - realignment: every value stays clickable, and picking an impossible
 *     one moves the *other* options onto values that make a real variant
 *   - URL sync via history.replaceState (?variant=ID)
 *   - hidden input [data-ec-variant-id] sync (consumed by the form)
 *   - dynamic update of price, stock, SKU and the gallery hero image
 *     when a different variant becomes active
 *
 * Layout-agnostic. No Shadow DOM. ARIA labels on unavailable values are
 * hydrated at runtime — server-rendered HTML stays semantic.
 */

class EcVariantPicker extends HTMLElement {
  #variants = [];
  #fieldsets = [];
  #onChange = null;

  connectedCallback() {
    this.#variants = this.#parseVariants();
    this.#fieldsets = Array.from(this.querySelectorAll('[data-ec-option-index]'));

    this.#onChange = (event) => this.#handleChange(event);
    this.addEventListener('change', this.#onChange);

    // Initial pass: apply availability based on the pre-checked options.
    this.#applyAvailability();
  }

  disconnectedCallback() {
    if (this.#onChange) this.removeEventListener('change', this.#onChange);
  }

  #parseVariants() {
    const raw = this.getAttribute('data-variants');
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.error('[ec-variant-picker] invalid variants JSON', err);
      return [];
    }
  }

  /** Currently selected option values, ordered by option index. */
  #currentSelection() {
    return this.#fieldsets.map((fs) => {
      const checked = fs.querySelector('[data-ec-option-input]:checked');
      return checked instanceof HTMLInputElement ? checked.value : null;
    });
  }

  #handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-ec-option-input]')) return;

    // Before anything reads the selection: the clicked value may not combine
    // with the other axes, in which case they move, not it.
    this.#realignOtherOptions(this.#optionIndexOf(target));

    this.#applyAvailability();

    const variant = this.#findVariant(this.#currentSelection());
    this.#syncDisplay(variant);
    this.#syncForm(variant);
    this.#syncUrl(variant);
    this.#dispatchUpdate(variant);
  }

  /** Fieldset rank (= option position - 1) of a given radio, -1 if foreign. */
  #optionIndexOf(radio) {
    return this.#fieldsets.findIndex((fs) => fs.contains(radio));
  }

  /**
   * Keeps the value the customer just picked and moves the *other* options
   * so the combination exists — without this, an impossible value is a dead
   * end the customer has to escape by guessing which other axis to change.
   * Among the variants carrying the picked value, the winner is the one
   * changing the fewest other options, then an available one over a sold-out
   * one, then the first in the product's own variant order.
   * No-op when the current selection already matches a variant.
   */
  #realignOtherOptions(optionIndex) {
    if (optionIndex < 0) return;
    const selection = this.#currentSelection();
    if (this.#findVariant(selection)) return;

    const picked = selection[optionIndex];
    if (picked == null) return;

    let best = null;
    let bestChanged = 0;
    let bestAvailable = false;

    this.#variants.forEach((variant) => {
      if (variant[`option${optionIndex + 1}`] !== picked) return;

      let changed = 0;
      selection.forEach((value, idx) => {
        if (idx === optionIndex || value == null) return;
        if (variant[`option${idx + 1}`] !== value) changed += 1;
      });

      const available = Boolean(variant.available);
      // Strict comparisons only, so iteration order settles every tie.
      const wins =
        best === null ||
        changed < bestChanged ||
        (changed === bestChanged && available && !bestAvailable);
      if (wins) {
        best = variant;
        bestChanged = changed;
        bestAvailable = available;
      }
    });

    if (!best) return;

    this.#fieldsets.forEach((fs, idx) => {
      if (idx === optionIndex) return;
      const value = best[`option${idx + 1}`];
      if (value == null) return;
      const radios = Array.from(fs.querySelectorAll('[data-ec-option-input]'));
      const match = radios.find(
        (radio) => radio instanceof HTMLInputElement && radio.value === value
      );
      // Setting .checked fires no change event: the caller stays the single
      // owner of the refresh, so price/media update once, not twice.
      if (match instanceof HTMLInputElement && !match.checked) match.checked = true;
    });
  }

  /**
   * Walks every option value and flags those that can't combine with the
   * currently checked values on the other axes.
   * Strict filter — once color A is picked, sizes that have no available
   * variant within color A get flagged unavailable on their <label>.
   * Single state, [data-ec-unavailable]: whether the combination is sold
   * out or does not exist at all, the value stays clickable (a click
   * realigns the other options), so neither radio.disabled nor
   * aria-disabled would describe it truthfully. The sr-only suffix added by
   * #syncUnavailableFlag carries the state to screen readers instead.
   */
  #applyAvailability() {
    const selection = this.#currentSelection();

    this.#fieldsets.forEach((fs, optionIndex) => {
      const radios = Array.from(fs.querySelectorAll('[data-ec-option-input]'));
      radios.forEach((radio) => {
        if (!(radio instanceof HTMLInputElement)) return;
        const candidate = selection.slice();
        candidate[optionIndex] = radio.value;

        const anyAvailable = this.#variants.some(
          (variant) =>
            variant.available &&
            candidate.every((value, idx) => value == null || variant[`option${idx + 1}`] === value)
        );

        const label = this.querySelector(`label[for='${radio.id}']`);

        // Every value stays enabled, mouse and keyboard alike.
        radio.disabled = false;
        if (label) {
          if (anyAvailable) label.removeAttribute('data-ec-unavailable');
          else label.setAttribute('data-ec-unavailable', '');
        }
        this.#syncUnavailableFlag(label, !anyAvailable);
      });
    });

    // Sync the displayed selected value in each legend.
    this.#fieldsets.forEach((fs) => {
      const display = fs.querySelector('[data-ec-option-value-display]');
      const checked = fs.querySelector('[data-ec-option-input]:checked');
      if (display && checked instanceof HTMLInputElement) {
        display.textContent = checked.value;
      }
    });
  }

  /**
   * Adds (or removes) a sr-only "Indisponible" suffix on a label so screen
   * readers announce the disabled state. CSS class `.ec-visually-hidden`
   * is defined in ec-product.css. Idempotent — safe to call repeatedly.
   */
  #syncUnavailableFlag(label, shouldShow) {
    if (!label) return;
    let flag = label.querySelector('[data-ec-unavailable-flag]');
    if (shouldShow) {
      if (!flag) {
        flag = document.createElement('span');
        flag.className = 'ec-visually-hidden';
        flag.setAttribute('data-ec-unavailable-flag', '');
        flag.textContent = ` (${this.#unavailableLabel()})`;
        label.appendChild(flag);
      }
    } else if (flag) {
      flag.remove();
    }
  }

  #unavailableLabel() {
    return this.getAttribute('data-i18n-unavailable') || 'Unavailable';
  }

  #findVariant(selection) {
    return this.#variants.find((variant) =>
      selection.every((value, idx) => value == null || variant[`option${idx + 1}`] === value)
    );
  }

  #syncForm(variant) {
    const formId = this.getAttribute('data-form-id');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return;

    const variantInput = form.querySelector('[data-ec-variant-id]');
    if (variantInput instanceof HTMLInputElement && variant) {
      variantInput.value = String(variant.id);
    }

    const cta = form.querySelector('[data-ec-add-to-cart]');
    const soldout = form.querySelector('[data-ec-soldout]');
    if (variant && variant.available) {
      if (cta instanceof HTMLElement) cta.hidden = false;
      if (soldout instanceof HTMLElement) soldout.hidden = true;
    } else {
      if (cta instanceof HTMLElement) cta.hidden = true;
      if (soldout instanceof HTMLElement) soldout.hidden = false;
    }
  }

  #syncDisplay(variant) {
    if (!variant) return;

    // Price strings are pre-rendered server-side with the Liquid `money`
    // filter (see snippets/ec-product-info.liquid → variants_json). That
    // matches whatever format the merchant configured for the shop —
    // no client-side currency/locale assumption needed.
    const priceRoot = document.querySelector('[data-ec-product-price]');
    if (priceRoot) {
      const formatted = variant.price_formatted || '';
      const compareFormatted =
        variant.compare_at && variant.compare_at > variant.price
          ? variant.compare_at_formatted || ''
          : '';

      const current = priceRoot.querySelector('.ec-product-info__price-current');
      const compare = priceRoot.querySelector('.ec-product-info__price-compare');
      if (current) current.textContent = formatted;
      if (compareFormatted) {
        if (compare) {
          compare.textContent = compareFormatted;
          compare.hidden = false;
        } else {
          const del = document.createElement('del');
          del.className = 'ec-product-info__price-compare';
          del.textContent = compareFormatted;
          priceRoot.prepend(del);
        }
      } else if (compare) {
        compare.hidden = true;
      }
    }

    // Stock.
    const stock = document.querySelector('[data-ec-product-stock]');
    if (stock instanceof HTMLElement) {
      const newState = variant.available ? 'available' : 'unavailable';
      stock.classList.remove('ec-product-stock--available', 'ec-product-stock--unavailable');
      stock.classList.add(`ec-product-stock--${newState}`);
      stock.dataset.ecStockState = newState;
      const label = stock.querySelector('[data-ec-stock-label]');
      if (label) {
        // Labels come from data-i18n-* on the stock element (locales),
        // same pattern as data-i18n-unavailable — no hardcoded strings.
        const next = variant.available ? stock.dataset.i18nInStock : stock.dataset.i18nSoldOut;
        if (next) label.textContent = next;
      }
    }

    // Gallery hero image — swap to variant.featured_image when set.
    if (variant.featured_image) {
      const mainImg = document.querySelector('.ec-product-gallery__main-img');
      if (mainImg instanceof HTMLImageElement) {
        mainImg.src = variant.featured_image;
        mainImg.removeAttribute('srcset');
      }
    }
  }

  #syncUrl(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url.toString());
  }

  #dispatchUpdate(variant) {
    if (!variant) return;
    this.dispatchEvent(
      new CustomEvent('ec:variant:update', {
        bubbles: true,
        detail: { variant },
      })
    );
  }
}

if (!customElements.get('ec-variant-picker')) {
  customElements.define('ec-variant-picker', EcVariantPicker);
}
